/**
 * Parsed JSON from referral / last-encounter document OCR (api/v1/ai/ocr).
 */
export interface ReferralDocOcrResult {
  ProviderName?: string;
  DOS?: string;
}

export const REFERRAL_DOC_OCR_PROMPT = `
You are a JSON API. Extract from this medical referral/order document:
- ProviderName: referring/ordering physician or provider name
- Last encounter DOS: date of service exactly as printed (e.g. "MM/DD/YYYY")
Return ONLY:
{ "ProviderName": "", "DOS": "" }
`.trim();

export interface LastCounterMedicationOcrItem {
  name: string;
  dosage?: string;
  frequency?: string;
}

export interface LastCounterMedsAllergiesOcrResult {
  Medications?: LastCounterMedicationOcrItem[];
  Allergies?: string[] | string;
}

export const LAST_COUNTER_MEDS_ALLERGIES_OCR_PROMPT = `
You are a JSON API. Extract from this medical document:
- Medications: each current medication with name, dosage/strength, and frequency when listed
- Allergies: each patient allergy (exclude "None", "NKDA", "No known allergies")

Return ONLY valid JSON:
{
  "Medications": [
    { "name": "", "dosage": "", "frequency": "" }
  ],
  "Allergies": [""]
}
`.trim();

/** Single-pass Last Counter OCR: summary + referral fields + meds/allergies. */
export interface LastCounterCombinedOcrResult {
  Summary?: string;
  ProviderName?: string;
  DOS?: string;
  Medications?: LastCounterMedicationOcrItem[];
  Allergies?: string[] | string;
}

export const LAST_COUNTER_COMBINED_OCR_PROMPT = `
You are a JSON API. Read this medical referral or last-encounter document and extract:

1. Summary: concise encounter summary (max 5 sentences, professional clinical language, no headings or bullets)
2. ProviderName: referring/ordering physician or provider name
3. DOS: date of service exactly as printed (e.g. "MM/DD/YYYY")
4. Medications: each current medication with name, dosage/strength, and frequency when listed
5. Allergies: each patient allergy (exclude "None", "NKDA", "No known allergies")

Return ONLY valid JSON:
{
  "Summary": "",
  "ProviderName": "",
  "DOS": "",
  "Medications": [
    { "name": "", "dosage": "", "frequency": "" }
  ],
  "Allergies": [""]
}
`.trim();

const PLACEHOLDER_ALLERGIES = new Set([
  "none",
  "nkda",
  "nka",
  "no known allergies",
  "no known drug allergies",
  "not mentioned",
  "nil",
  "unknown",
]);

export function normalizeLastCounterAllergiesFromOcr(raw: unknown): string[] {
  const items: string[] = [];
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      items.push(String(entry ?? "").trim());
    }
  } else if (typeof raw === "string") {
    items.push(...raw.split(",").map((part) => part.trim()));
  }
  return items.filter(
    (name) => name.length > 0 && !PLACEHOLDER_ALLERGIES.has(name.toLowerCase())
  );
}

function readOcrField(obj: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (obj[key] != null && String(obj[key]).trim() !== "") {
      return obj[key];
    }
  }
  const byLower = new Map<string, unknown>();
  for (const [key, value] of Object.entries(obj)) {
    byLower.set(key.toLowerCase(), value);
  }
  for (const key of keys) {
    const value = byLower.get(key.toLowerCase());
    if (value != null && String(value).trim() !== "") {
      return value;
    }
  }
  return undefined;
}

/** Normalize raw OCR JSON (any key casing/shape) into LastCounterCombinedOcrResult. */
export function normalizeLastCounterCombinedOcrResult(
  parsed: Record<string, unknown>
): LastCounterCombinedOcrResult {
  const medicationsRaw = readOcrField(parsed, [
    "Medications",
    "medications",
    "Medication",
    "medication",
    "CurrentMedications",
    "currentMedications",
  ]);
  const allergiesRaw = readOcrField(parsed, [
    "Allergies",
    "allergies",
    "Allergy",
    "allergy",
  ]);

  return {
    Summary: String(
      readOcrField(parsed, ["Summary", "summary", "EncounterSummary"]) ?? ""
    ).trim(),
    ProviderName: String(
      readOcrField(parsed, [
        "ProviderName",
        "providerName",
        "provider_name",
        "Provider",
        "provider",
      ]) ?? ""
    ).trim(),
    DOS: String(
      readOcrField(parsed, [
        "DOS",
        "dos",
        "DateOfService",
        "dateOfService",
        "date_of_service",
      ]) ?? ""
    ).trim(),
    Medications: normalizeLastCounterMedicationsFromOcr(medicationsRaw),
    Allergies: normalizeLastCounterAllergiesFromOcr(allergiesRaw),
  };
}

export function normalizeLastCounterMedicationsFromOcr(
  raw: unknown
): LastCounterMedicationOcrItem[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const medications: LastCounterMedicationOcrItem[] = [];
  for (const entry of raw) {
    if (typeof entry === "string") {
      const name = entry.trim();
      if (name) {
        medications.push({ name, dosage: "", frequency: "" });
      }
      continue;
    }
    if (!entry || typeof entry !== "object") {
      continue;
    }
    const row = entry as Record<string, unknown>;
    const name = String(
      row["name"] ??
        row["Name"] ??
        row["medication"] ??
        row["Medication"] ??
        row["drug"] ??
        row["Drug"] ??
        ""
    ).trim();
    if (!name) {
      continue;
    }
    medications.push({
      name,
      dosage: String(
        row["dosage"] ??
          row["Dosage"] ??
          row["qty or dosage"] ??
          row["strength"] ??
          row["Strength"] ??
          ""
      ).trim(),
      frequency: String(
        row["frequency"] ?? row["Frequency"] ?? row["sig"] ?? row["Sig"] ?? ""
      ).trim(),
    });
  }
  return medications;
}

export function isReferralDocPdfAccepted(file: File): boolean {
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  return type === "application/pdf" || name.endsWith(".pdf");
}
